import { useState } from 'preact/hooks';
import { useAuth } from '../lib/auth';

export function UpgradePage() {
  const { subscription, workspace } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const isTrialing = subscription?.status === 'trialing';
  const isActive = subscription?.status === 'active';
  const currentPlan = subscription?.plan || 'trial';

  async function handleUpgrade(plan) {
    setLoading(plan);
    setError(null);

    try {
      const sessionId = localStorage.getItem('sessionId');
      const res = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({ plan })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  }

  return (
    <div class="upgrade-page">
      <div class="upgrade-header">
        <h1>Choose Your Plan</h1>
        <p>Simple, transparent pricing. No per-user fees.</p>
      </div>

      {error && (
        <div class="error-banner">
          {error}
        </div>
      )}

      <div class="plans-container">
        {/* Free Plan */}
        <div class={`plan-card ${currentPlan === 'trial' && isTrialing ? 'current' : ''}`}>
          <div class="plan-header">
            <h2>Free</h2>
            <div class="plan-price">
              <span class="currency">€</span>
              <span class="amount">0</span>
              <span class="period">/month</span>
            </div>
          </div>
          <div class="plan-description">
            Perfect for small teams getting started
          </div>
          <ul class="plan-features">
            <li><span class="check">✓</span> Up to 5 users</li>
            <li><span class="check">✓</span> Unlimited leave requests</li>
            <li><span class="check">✓</span> Slack integration</li>
            <li><span class="check">✓</span> Team calendar</li>
            <li><span class="check">✓</span> Basic reporting</li>
          </ul>
          <div class="plan-action">
            {isTrialing && (
              <span class="current-badge">Current Plan</span>
            )}
          </div>
        </div>

        {/* Pro Plan */}
        <div class={`plan-card featured ${currentPlan === 'pro' && isActive ? 'current' : ''}`}>
          <div class="featured-badge">Most Popular</div>
          <div class="plan-header">
            <h2>Pro</h2>
            <div class="plan-price">
              <span class="currency">€</span>
              <span class="amount">39</span>
              <span class="period">/month</span>
            </div>
          </div>
          <div class="plan-description">
            For growing teams who need more control
          </div>
          <ul class="plan-features">
            <li><span class="check">✓</span> Up to 50 users</li>
            <li><span class="check">✓</span> Everything in Free, plus:</li>
            <li><span class="check">✓</span> Wallchart view</li>
            <li><span class="check">✓</span> Custom leave types</li>
            <li><span class="check">✓</span> Approval workflows</li>
            <li><span class="check">✓</span> Email notifications</li>
            <li><span class="check">✓</span> Export to CSV/Excel</li>
            <li><span class="check">✓</span> Priority support</li>
          </ul>
          <div class="plan-action">
            {currentPlan === 'pro' && isActive ? (
              <span class="current-badge">Current Plan</span>
            ) : (
              <button
                class="btn btn-primary btn-lg"
                onClick={() => handleUpgrade('pro')}
                disabled={loading === 'pro'}
              >
                {loading === 'pro' ? 'Loading...' : 'Upgrade to Pro'}
              </button>
            )}
          </div>
        </div>

        {/* Business Plan */}
        <div class={`plan-card ${currentPlan === 'business' && isActive ? 'current' : ''}`}>
          <div class="plan-header">
            <h2>Business</h2>
            <div class="plan-price">
              <span class="currency">€</span>
              <span class="amount">99</span>
              <span class="period">/month</span>
            </div>
          </div>
          <div class="plan-description">
            For large teams with advanced needs
          </div>
          <ul class="plan-features">
            <li><span class="check">✓</span> Unlimited users</li>
            <li><span class="check">✓</span> Everything in Pro, plus:</li>
            <li><span class="check">✓</span> Multi-team support</li>
            <li><span class="check">✓</span> Advanced analytics</li>
            <li><span class="check">✓</span> API access</li>
            <li><span class="check">✓</span> Custom integrations</li>
            <li><span class="check">✓</span> Dedicated support</li>
            <li><span class="check">✓</span> SLA guarantee</li>
          </ul>
          <div class="plan-action">
            {currentPlan === 'business' && isActive ? (
              <span class="current-badge">Current Plan</span>
            ) : (
              <button
                class="btn btn-secondary btn-lg"
                onClick={() => handleUpgrade('business')}
                disabled={loading === 'business'}
              >
                {loading === 'business' ? 'Loading...' : 'Upgrade to Business'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div class="pricing-footer">
        <div class="guarantee">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <div>
            <strong>30-day money-back guarantee</strong>
            <p>Not satisfied? Get a full refund, no questions asked.</p>
          </div>
        </div>

        <div class="faq">
          <h3>Frequently Asked Questions</h3>

          <details>
            <summary>Can I change plans later?</summary>
            <p>Yes, you can upgrade or downgrade at any time. Changes take effect immediately and are prorated.</p>
          </details>

          <details>
            <summary>What payment methods do you accept?</summary>
            <p>We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment provider Stripe.</p>
          </details>

          <details>
            <summary>Is there a contract or commitment?</summary>
            <p>No, all plans are month-to-month. You can cancel anytime with no penalties.</p>
          </details>

          <details>
            <summary>What happens when I reach my user limit?</summary>
            <p>You'll be prompted to upgrade to a higher plan. Existing users will continue to have access.</p>
          </details>
        </div>

        <p class="contact-note">
          Have questions? Contact us at{' '}
          <a href="mailto:support@maltashopper.com">support@maltashopper.com</a>
        </p>
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .upgrade-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }

  .upgrade-header {
    text-align: center;
    margin-bottom: 3rem;
  }

  .upgrade-header h1 {
    font-size: 2.5rem;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  .upgrade-header p {
    font-size: 1.25rem;
    color: #64748b;
  }

  .error-banner {
    background: #fef2f2;
    color: #dc2626;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    text-align: center;
  }

  .plans-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
  }

  .plan-card {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    border: 2px solid #e2e8f0;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .plan-card.featured {
    border-color: #3b82f6;
    box-shadow: 0 10px 40px rgba(59, 130, 246, 0.15);
  }

  .plan-card.current {
    border-color: #22c55e;
  }

  .featured-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: #3b82f6;
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .plan-header {
    text-align: center;
    margin-bottom: 1rem;
  }

  .plan-header h2 {
    font-size: 1.5rem;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  .plan-price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
  }

  .plan-price .currency {
    font-size: 1.5rem;
    color: #64748b;
  }

  .plan-price .amount {
    font-size: 3rem;
    font-weight: 700;
    color: #1e293b;
  }

  .plan-price .period {
    font-size: 1rem;
    color: #64748b;
  }

  .plan-description {
    text-align: center;
    color: #64748b;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
  }

  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    flex: 1;
  }

  .plan-features li {
    padding: 0.5rem 0;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .plan-features .check {
    color: #22c55e;
    font-weight: bold;
  }

  .plan-action {
    text-align: center;
  }

  .current-badge {
    display: inline-block;
    background: #dcfce7;
    color: #16a34a;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 500;
  }

  .btn {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    border: none;
    font-size: 1rem;
    transition: all 0.2s;
  }

  .btn-lg {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  .btn-primary {
    background: #3b82f6;
    color: white;
  }

  .btn-primary:hover {
    background: #2563eb;
  }

  .btn-secondary {
    background: #1e293b;
    color: white;
  }

  .btn-secondary:hover {
    background: #0f172a;
  }

  .btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .pricing-footer {
    max-width: 700px;
    margin: 0 auto;
  }

  .guarantee {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #f0fdf4;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
  }

  .guarantee svg {
    width: 40px;
    height: 40px;
    color: #22c55e;
    flex-shrink: 0;
  }

  .guarantee strong {
    display: block;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }

  .guarantee p {
    color: #64748b;
    margin: 0;
    font-size: 0.875rem;
  }

  .faq {
    margin-bottom: 2rem;
  }

  .faq h3 {
    font-size: 1.25rem;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  .faq details {
    border-bottom: 1px solid #e2e8f0;
  }

  .faq summary {
    padding: 1rem 0;
    cursor: pointer;
    font-weight: 500;
    color: #1e293b;
  }

  .faq summary:hover {
    color: #3b82f6;
  }

  .faq details p {
    padding: 0 0 1rem 0;
    color: #64748b;
    margin: 0;
  }

  .contact-note {
    text-align: center;
    color: #94a3b8;
  }

  .contact-note a {
    color: #3b82f6;
  }

  @media (max-width: 768px) {
    .upgrade-header h1 {
      font-size: 1.75rem;
    }

    .plan-price .amount {
      font-size: 2.5rem;
    }
  }
`;
